/**
 * Procurement › Route to Buy (2026-10-13).
 *
 * How one market actually buys — through a framework / consortium vehicle, through a reseller, or
 * direct — which vehicles carry it, who sells through each, and the resellers in it. Everything is
 * computed live server-side from closed_deals + deal_route_vehicles (procurement_route_to_buy); no
 * model is involved, so the numbers move as soon as the deals do.
 *
 * "Your categories" is the tenant's own category scope (the same one Displacement uses), falling
 * back to the page's category filter. "Are you on it?" / "Carries your product?" come from the
 * tenant's own product names on the server (tenantFamily.ownProductNames).
 *
 * The mix shares are of deals whose ROUTE IS KNOWN (route_to_market_mix's definition); the panel
 * says how many that is, so the denominator is never hidden.
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2, AlertCircle, ChevronDown, Info } from 'lucide-react';
import { fetchRouteToBuy, fetchRouteVerdicts } from '@/lib/api/procurementServer';
import { expandCategoriesForApi } from '@/utils/edtechCategoryNames';
import { resolveDisplacementScope } from '@/lib/displacementTenantScopes';
import { useTranslation, translate } from '@/components/utils/translations';

// Display labels are translation keys, resolved with t() at render time.
const ROUTE_TYPE_LABEL_KEY = {
  framework: 'procRtbRouteFramework', consortium: 'procRtbRouteConsortium', cooperative: 'procRtbRouteCooperative',
  buying_org: 'procRtbRouteBuyingOrg', programme: 'procRtbRouteProgramme',
};
const MIX_ROWS = [
  { key: 'via_framework', labelKey: 'procRtbThroughFramework', color: '#00C2C2' },
  { key: 'via_reseller', labelKey: 'procRtbThroughReseller', color: '#6FA8DC' },
  { key: 'direct', labelKey: 'procRtbDirectToSupplier', color: '#0A1A2F' },
];
// Results kept for the browser session, keyed by tenant + market + lens, so a market already
// opened (or prefetched) switches instantly. The server holds them for 24 h as well.
const RESULT_CACHE = new Map();
const cacheKey = (tenantId, country, cats) => `${tenantId}|${country}|${(cats || []).join('|')}`;
function loadRouteToBuy(tenantId, country, cats) {
  const k = cacheKey(tenantId, country, cats);
  if (!RESULT_CACHE.has(k)) {
    const p = fetchRouteToBuy(tenantId, country, cats).catch((e) => { RESULT_CACHE.delete(k); throw e; });
    RESULT_CACHE.set(k, p);
  }
  return RESULT_CACHE.get(k);
}

// Markets (Tiago, 2026-10-27). The chips used to be the server's top 10 by deals HELD — which ranks
// how completely each register was collected, not which markets matter — so they showed Czechia and
// Mexico and never Spain or France. Now: two default chips, and every other market in one dropdown so
// a long list stays easy to switch across. The page's own Country filter still overrides both.
// Figures cover every buyer in the market, all sectors, not only higher education.
// 2026-11-03: these two are the buttons; every other evidenced market sits in the Other countries dropdown.
const DEFAULT_MARKETS = ['United Kingdom', 'United States'];
const MORE_MARKETS = [
  'Australia', 'Brazil', 'Canada', 'Chile', 'Colombia', 'Czechia', 'Denmark', 'Dominican Republic', 'Ecuador',
  'Finland', 'France', 'Germany', 'Hungary', 'Ireland', 'Italy', 'Malaysia', 'Mexico', 'Netherlands',
  'Panama', 'Peru', 'Poland', 'Portugal', 'Singapore', 'Spain', 'Sweden', 'Switzerland', 'Uruguay',
];

// Markets deliberately kept off this tab. Canada was hidden on 2026-10-16 because
// route_to_buy_thresholds had no Canada row, so the threshold-bands half rendered empty; its bands
// were added the same day from Contracting Policy Notice 2025-8 (CFTA/CETA, 2026-2027) and Tiago
// asked for it back, so the set is empty again. Keep the mechanism: it is the one place to drop a
// market whose bands are missing.
const MARKET_HIDDEN = new Set([]);

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-GB'));
// Deal values are not shown on this tab: pricing lives in the pricing chart (Tiago, 2026-11-02).

function Pill({ on, onClick, children, disabled, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`min-h-[40px] px-4 rounded-full text-sm border transition-colors ${
        on ? 'bg-[#0A1A2F] text-white border-[#0A1A2F] font-semibold' : 'bg-white text-[#0A1A2F] border-gray-300 hover:border-[#0A1A2F]'
      } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
    >
      {children}
    </button>
  );
}

function LensToggle({ mine, setMine, canMine }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label={t('productFitCategories')}>
      <Pill on={!mine} onClick={() => setMine(false)}>{t('procAllCategories')}</Pill>
      <Pill on={mine} onClick={() => setMine(true)} disabled={!canMine} title={canMine ? undefined : t('procRtbSelectCategoriesForLens')}>
        {t('procYourCategories')}
      </Pill>
    </div>
  );
}

function Bars({ items, color = '#0A1A2F', track = 'bg-gray-100' }) {
  const max = Math.max(1, ...items.map((i) => i.n || 0));
  return (
    <div className="space-y-2">
      {items.map((i) => (
        <div key={i.name} className="flex items-center gap-3">
          <span className="w-44 shrink-0 truncate text-sm" title={i.name}>{i.name}</span>
          <div className={`flex-1 h-2 rounded ${track} overflow-hidden`}>
            <div className="h-2 rounded" style={{ width: `${Math.round((100 * (i.n || 0)) / max)}%`, background: color }} />
          </div>
          <span className="w-12 text-right font-mono text-xs">{fmt(i.n)}</span>
        </div>
      ))}
    </div>
  );
}

// ── Verdicts (2026-11-02) ─────────────────────────────────────────────────────────────────────────
// The conclusions on top of the evidence below: local entity, frameworks to join, friendliness of public
// institutions, public share and LMS/SIS integrations. Precomputed server-side (rtm_verdict_cache); a verdict
// that does not clear its evidence bar is not shown at all (Tiago). No deal values or renewals here — pricing
// lives in the pricing chart. Verdicts are cached per tenant + market for the browser session.
const VERDICT_CACHE = new Map();
function loadVerdicts(tenantId, country) {
  const k = `${tenantId}|${country || ''}`;
  if (!VERDICT_CACHE.has(k)) {
    const p = fetchRouteVerdicts(tenantId, country).catch((e) => { VERDICT_CACHE.delete(k); throw e; });
    VERDICT_CACHE.set(k, p);
  }
  return VERDICT_CACHE.get(k);
}
const LOCAL_ENTITY_KEY = {
  not_required: 'procRtbLeNotRequired', required_for_some_routes: 'procRtbLeSomeRoutes',
  required: 'procRtbLeRequired', practically_required: 'procRtbLeEffectively', unknown: 'procRtbLeUnknown',
};
const MONTHS_EN = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Hover / keyboard-focus definition. A real <button> so it is reachable by Tab; the text is also its aria-label.
function InfoTip({ text }) {
  if (!text) return null;
  return (
    <span className="relative inline-flex group align-middle">
      <button type="button" aria-label={text} className="inline-flex h-6 w-6 items-center justify-center rounded-full text-gray-400 hover:text-[#007F80] focus:text-[#007F80] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2C2]">
        <Info className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      <span role="tooltip" className="pointer-events-none invisible absolute left-1/2 top-7 z-30 w-80 max-w-[80vw] -translate-x-1/2 rounded-lg bg-[#0A1A2F] px-3 py-2 text-left text-xs font-normal normal-case leading-relaxed tracking-normal text-white opacity-0 shadow-lg transition-opacity group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
        {text}
      </span>
    </span>
  );
}

function VerdictTiles({ v, t }) {
  if (!v) return null;
  const tiles = [];
  const le = v.v1_local_entity;
  if (le?.verdict) {
    tiles.push({ k: t('procRtbTileLocalEntity'), a: t(LOCAL_ENTITY_KEY[le.verdict] || 'procRtbLeUnknown'), s: t('procRtbConfidence', { c: le.confidence || '—' }), tip: t('procRtbTipLocalEntity') });
  }
  const fw = v.v3_frameworks;
  if (fw?.status === 'ok') {
    const toJoin = (fw.all || []).filter((j) => j.you !== 'named_supplier' && j.you !== 'via_reseller');
    const withComp = toJoin.filter((j) => (j.competitors_on_it || []).length).length;
    tiles.push({ k: t('procRtbTileFrameworks'), a: t('procRtbToJoinN', { n: toJoin.length }), s: withComp ? t('procRtbWithCompetitorN', { n: withComp }) : t('procRtbNoCompetitorListed'), tip: t('procRtbTipFrameworks') });
  }
  const fr = v.v4_friendliness;
  if (fr?.public_pct != null) {
    const adv = (fr.advice || []).join(' ');
    tiles.push({ k: t('procRtbTileFriendliness'), a: `${fr.public_pct}%`, s: fr.cycle ? fr.cycle.charAt(0).toUpperCase() + fr.cycle.slice(1) : '', tip: `${t('procRtbTipFriendliness')}${adv ? ` ${t('procRtbPlanFor')} ${adv}` : ''} ${t('procRtbBasedOnPublic', { n: fr.public_n })}` });
  }
  if (v.public_share?.pct != null) {
    tiles.push({ k: t('procRtbTilePublicShare'), a: `${v.public_share.pct}%`, s: t('procRtbTeachingInstitutionsN', { n: v.public_share.n }), tip: t('procRtbTipPublicShare') });
  }
  const integ = (v.v5_partners?.categories || []).filter((c) => c.ok && (c.partners || []).length);
  if (integ.length) {
    const line = integ.map((c) => `${c.category.toUpperCase()}: ${c.partners.slice(0, 2).map((p) => `${p.vendor} ${p.share_pct}%`).join(', ')}`).join(' · ');
    tiles.push({ k: t('procRtbTileIntegrations'), a: line, s: '', tip: t('procRtbTipIntegrations'), small: true });
  }
  if (!tiles.length) return null;
  return (
    <section aria-label={t('procRtbVerdicts')} className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(200px,1fr))]">
      {tiles.map((x) => (
        <div key={x.k} className="rounded-xl border border-gray-200 bg-white px-4 py-3 space-y-1">
          <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-gray-500"><span>{x.k}</span><InfoTip text={x.tip} /></div>
          <div className={`${x.small ? 'text-sm font-semibold' : 'text-lg font-bold'} text-[#0A1A2F] leading-snug`}>{x.a}</div>
          {x.s ? <div className="text-xs text-gray-600">{x.s}</div> : null}
        </div>
      ))}
    </section>
  );
}

function MarketSignals({ v, t }) {
  const ins = v?.insights;
  if (!ins) return null;
  const items = [];
  const top = ins.contract_start_months?.top || [];
  if (top.length) items.push({ k: t('procRtbContractStartMonths'), v: top.map((m) => `${MONTHS_EN[m.month]} ${m.share_pct}%`).join(' · '), tip: t('procRtbTipContractStart') });
  if (ins.bidders?.median != null) items.push({ k: t('procRtbBiddersPerTender'), v: t('procRtbBiddersLine', { m: ins.bidders.median, s: ins.bidders.single_bidder_pct }), tip: t('procRtbTipBidders') });
  if (!items.length) return null;
  return (
    <section aria-label={t('procRtbMarketSignals')} className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
      {items.map((x) => (
        <div key={x.k} className="rounded-xl border border-gray-200 bg-white px-4 py-3">
          <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-gray-500"><span>{x.k}</span><InfoTip text={x.tip} /></div>
          <div className="mt-1 text-sm font-semibold text-[#0A1A2F]">{x.v}</div>
        </div>
      ))}
    </section>
  );
}

const YOU_KEY = { named_supplier: 'procRtbYouOnIt', via_reseller: 'procRtbYouViaReseller', not_listed: 'procRtbYouNotListed', not_assessed: 'procRtbYouToJoin' };
const YOU_STYLE = {
  named_supplier: 'bg-[#E6F8F8] text-[#00565A]', via_reseller: 'bg-[#E6F8F8] text-[#00565A]',
  not_listed: 'bg-[#EEF2F7] text-[#1F3A5F]', not_assessed: 'bg-[#EEF2F7] text-[#1F3A5F]',
};

export default function RouteToBuy({ tenantId, categories = [], countries: pageCountries = [] }) {
  const { t } = useTranslation();
  const scope = useMemo(() => resolveDisplacementScope(tenantId), [tenantId]);
  const myCats = useMemo(
    () => (scope?.categories?.length ? scope.categories : (categories || [])),
    [scope, categories],
  );
  const myCatsApi = useMemo(() => expandCategoriesForApi(myCats), [myCats]);
  const canMine = myCatsApi.length > 0;

  const [mine, setMine] = useState(false);
  const [country, setCountry] = useState(
    (pageCountries || []).find((c) => !MARKET_HIDDEN.has(c))
    || (scope?.countries || []).find((c) => !MARKET_HIDDEN.has(c))
    || 'United Kingdom',
  );
  // The page's own Country filter drives the market: picking countries there switches the tab to
  // the first one and the chips become exactly that selection (2026-10-13: Tiago toggled the page
  // filter and the tab, which ignored it, appeared not to refresh).
  const pageCountryKey = (pageCountries || []).join('|');
  useEffect(() => {
    const list = (pageCountryKey ? pageCountryKey.split('|') : []).filter((c) => !MARKET_HIDDEN.has(c));
    if (list.length && !list.includes(country)) setCountry(list[0]);
  }, [pageCountryKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vehicleIdx, setVehicleIdx] = useState(0);
  const [resellerName, setResellerName] = useState(null);
  // Verdicts for the selected market, and the list of markets with enough evidence to be offered at all
  // (rtm_country_readiness.included). Until the list arrives the old chips stay, so the tab never blanks.
  const [verdict, setVerdict] = useState(null);
  const [evidenced, setEvidenced] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!tenantId || !country) return undefined;
    setVerdict(null);
    loadVerdicts(tenantId, country)
      .then((d) => { if (!cancelled) { setVerdict(d); if (Array.isArray(d?.markets)) setEvidenced(d.markets); } })
      .catch((e) => { if (!cancelled && Array.isArray(e?.markets)) setEvidenced(e.markets); });
    return () => { cancelled = true; };
  }, [tenantId, country]);
  useEffect(() => {
    if (evidenced?.length && !evidenced.includes(country)) {
      const fromPage = (pageCountryKey ? pageCountryKey.split('|') : []).find((c) => evidenced.includes(c));
      setCountry(fromPage || (evidenced.includes('United Kingdom') ? 'United Kingdom' : evidenced[0]));
    }
  }, [evidenced]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    if (!tenantId || !country) return undefined;
    setLoading(true);
    setError(null);
    loadRouteToBuy(tenantId, country, mine && canMine ? myCatsApi : [])
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setVehicleIdx(0);
        setResellerName(d?.resellers?.[0]?.name ?? null);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message || translate('procFailedToLoad'));
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [tenantId, country, mine, canMine, myCatsApi]);

  // The chips: the page's Country selection when there is one, otherwise the two defaults. A market
  // picked from the dropdown shows as the dropdown's own (highlighted) value, not as a third chip.
  // 2026-11-02: once the evidenced-market list is known, the chips are exactly those markets (or the page's
  // Country selection within them) and the dropdown goes — a market without enough evidence is not offered.
  const pageList = pageCountryKey ? pageCountryKey.split('|').filter((c) => !MARKET_HIDDEN.has(c)) : [];
  // 2026-11-03 (Tiago): UK and US as the buttons, every other evidenced market in an "Other countries"
  // dropdown beside them — the 13 evidenced markets as chips made a long row. The evidence gate stays: the
  // dropdown lists only markets in the evidenced list once it is known.
  const pageInEvidence = evidenced?.length ? pageList.filter((c) => evidenced.includes(c)) : pageList;
  const chipList = pageInEvidence.length
    ? pageInEvidence
    : (evidenced?.length ? DEFAULT_MARKETS.filter((c) => evidenced.includes(c)) : DEFAULT_MARKETS);
  // Once a market has loaded, fetch the other CHIPS for the same lens in the background, two at a
  // time, so switching between them is instant. The dropdown is not prefetched — eighteen markets
  // per lens is too many requests to fire on every visit; the server cache already holds them.
  const chipKey = chipList.join('|');
  useEffect(() => {
    if (!tenantId || !chipKey) return undefined;
    let stop = false;
    const cats = mine && canMine ? myCatsApi : [];
    const queue = chipKey.split('|').filter((c) => c && c !== country);
    const worker = async () => {
      while (!stop && queue.length) {
        const c = queue.shift();
        try { await loadRouteToBuy(tenantId, c, cats); } catch { /* a failed prefetch is retried on click */ }
      }
    };
    worker(); worker();
    return () => { stop = true; };
  }, [tenantId, chipKey, mine, canMine, myCatsApi]); // eslint-disable-line react-hooks/exhaustive-deps

  const mix = data?.mix;
  const vehicles = data?.vehicles || [];
  const resellers = data?.resellers || [];
  const vehicle = vehicles[vehicleIdx] || null;
  // "Your categories" shows the researched vehicles for this market with your status on each (public supplier
  // lists only); "All categories" keeps the deal-derived view of the whole market.
  const researchVeh = verdict?.v3_frameworks?.all || [];
  const showResearch = mine && researchVeh.length > 0;
  const rVehicle = researchVeh[vehicleIdx] || researchVeh[0] || null;
  const reseller = resellers.find((r) => r.name === resellerName) || resellers[0] || null;
  const moreMarkets = (evidenced?.length ? evidenced : MORE_MARKETS)
    .filter((c) => !MARKET_HIDDEN.has(c) && !chipList.includes(c))
    .sort((a, b) => a.localeCompare(b));
  // While a new market or lens loads, the old figures stay on screen — dim them so the change is
  // visible at once instead of looking like nothing happened.
  const busy = loading ? 'opacity-40 pointer-events-none transition-opacity' : 'transition-opacity';
  const hasOwnProducts = (data?.own_products || []).length > 0;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#0A1A2F]">{t('routeToBuy')}</h2>
          <p className="text-sm text-gray-600">{t('procRtbSubtitle')}</p>
          {loading ? <p className="mt-1 flex items-center gap-2 text-sm text-[#007F80]"><Loader2 className="h-4 w-4 animate-spin" /> {t('procRtbLoadingCountry', { country })}</p> : null}
        </div>
        <div className="flex flex-col items-end gap-2">
        {/* Same "last updated" mark as the Displacement Radar header (Tiago, 2026-11-03). */}
        {data?.computed_at ? (
          <div className="whitespace-nowrap text-right text-xs text-muted-foreground">
            <span className="mr-1.5 inline-block h-[7px] w-[7px] rounded-full bg-[#00C2C2] align-middle" />
            {t('procLastUpdatedDate', { date: String(data.computed_at).slice(0, 10) })}
          </div>
        ) : null}
        <div className="flex flex-wrap items-center justify-end gap-2" role="group" aria-label={t('market')}>
          {chipList.map((c) => (
            <Pill key={c} on={c === country} onClick={() => setCountry(c)}>{c}</Pill>
          ))}
          {/* The native arrow sat flush against the rounded edge and was clipped (Tiago, 2026-10-27):
              hide it and draw our own chevron inside the right padding. */}
          {moreMarkets.length === 0 ? null : (
          <span className="relative inline-flex">
            <select
              aria-label={t('procRtbMoreCountries')}
              value={moreMarkets.includes(country) ? country : ''}
              onChange={(e) => { if (e.target.value) setCountry(e.target.value); }}
              className={`min-h-[40px] appearance-none rounded-full border pl-4 pr-10 text-sm bg-white text-[#0A1A2F] cursor-pointer ${
                moreMarkets.includes(country) ? 'border-[#0A1A2F] font-semibold' : 'border-gray-300 hover:border-[#0A1A2F]'
              }`}
            >
              <option value="">{t('procRtbMoreCountriesOption')}</option>
              {moreMarkets.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <ChevronDown aria-hidden="true" className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#0A1A2F]" />
          </span>
          )}
        </div>
        </div>
      </div>

      <VerdictTiles v={verdict} t={t} />

      {error ? (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      ) : null}

      <section className={`rounded-xl border border-gray-200 bg-white p-5 space-y-4 ${busy}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">{t('procRtbHowCountryBuys', { country })}</h3>
          <LensToggle mine={mine} setMine={setMine} canMine={canMine} />
        </div>
        {loading && !data ? (
          <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> {t('procLoading')}</div>
        ) : mix && !(mix.route_known > 0) ? (
          <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-600">{t('procRtbNotEnoughRouteDeals')}</div>
        ) : mix ? (
          <>
            <div className="space-y-3">
              {MIX_ROWS.map((r) => (
                <div key={r.key} className="space-y-1">
                  <div className="flex justify-between text-sm"><span>{t(r.labelKey)}</span><span className="font-mono">{mix[r.key] ?? 0}%</span></div>
                  <div className="h-2.5 rounded bg-gray-100 overflow-hidden">
                    <div className="h-2.5 rounded" style={{ width: `${Math.min(100, mix[r.key] ?? 0)}%`, background: r.color }} />
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : null}
        {loading && data ? <div className="flex items-center gap-2 text-xs text-gray-500"><Loader2 className="h-3 w-3 animate-spin" /> {t('procUpdating')}</div> : null}
      </section>

      <div className={`flex flex-wrap gap-4 items-start ${busy}`}>
        <section className="flex-[1_1_420px] min-w-0 rounded-xl border border-gray-200 bg-white p-5 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">{t('procRtbVehiclesThatMatter')}</h3>
            <LensToggle mine={mine} setMine={setMine} canMine={canMine} />
          </div>
          {showResearch ? researchVeh.map((v, i) => (
            <button
              key={v.id || v.name}
              type="button"
              onClick={() => setVehicleIdx(i)}
              className={`w-full text-left rounded-lg px-4 py-3 border space-y-1.5 ${i === vehicleIdx ? 'border-2 border-[#00C2C2] bg-[#E6F8F8]' : 'border-gray-200 bg-white hover:border-gray-400'}`}
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-semibold text-[#0A1A2F]">{v.display || v.name}</span>
                <span className="text-xs text-gray-500 whitespace-nowrap">{ROUTE_TYPE_LABEL_KEY[v.route_type] ? t(ROUTE_TYPE_LABEL_KEY[v.route_type]) : (v.route_type || '').replace('_', ' ')}</span>
              </div>
              {v.owner ? <div className="text-xs text-gray-500">{v.owner}</div> : null}
              <div className="flex flex-wrap gap-1.5 text-xs">
                <span className={`rounded-full px-2 py-0.5 ${YOU_STYLE[v.you] || 'bg-gray-100 text-gray-600'}`}>{t(YOU_KEY[v.you] || 'procRtbYouToJoin')}</span>
                {(v.competitors_on_it || []).length ? <span className="rounded-full bg-[#FDF1E4] px-2 py-0.5 text-[#8A4A0F]">{t('procRtbCompetitorOnIt')}: {v.competitors_on_it[0]}</span> : null}
                {v.end_date ? <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">{t('procRtbEnds', { d: String(v.end_date).slice(0, 7) })}</span> : null}
              </div>
            </button>
          )) : null}
          {showResearch && !researchVeh.length ? (
            <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-600">{t('procRtbNoVehicleForCategories', { country })}</div>
          ) : null}
          {!showResearch && !loading && !vehicles.length ? (
            <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-600">
              {mine ? t('procRtbNoFrameworkMine', { country }) : t('procRtbNoFrameworkAll', { country })}
            </div>
          ) : null}
          {!showResearch && vehicles.map((v, i) => (
            <button
              key={v.name}
              type="button"
              onClick={() => setVehicleIdx(i)}
              className={`w-full text-left rounded-lg px-4 py-3 border space-y-1.5 ${i === vehicleIdx ? 'border-2 border-[#00C2C2] bg-[#E6F8F8]' : 'border-gray-200 bg-white hover:border-gray-400'}`}
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-semibold text-[#0A1A2F]">{v.name}</span>
                <span className="text-xs text-gray-500 whitespace-nowrap">{ROUTE_TYPE_LABEL_KEY[v.route_type] ? t(ROUTE_TYPE_LABEL_KEY[v.route_type]) : v.route_type}</span>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-700">
                <span><b className="font-mono">{fmt(v.deals)}</b> {t('procRtbDealsLower')}</span>
                <span><b className="font-mono">{fmt(v.buyers)}</b> {t('procRtbBuyersLower')}</span>
                <span><b className="font-mono">{fmt(v.suppliers)}</b> {t('procRtbSuppliersLower')}</span>
              </div>
            </button>
          ))}
        </section>

        <section className="flex-[1_1_420px] min-w-0 rounded-xl border-2 border-[#00C2C2] bg-white p-5 space-y-4">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[#007F80]">{t('procRtbInsideVehicle')}</h3>
            <div className="text-lg font-bold text-[#0A1A2F]">{(showResearch ? (rVehicle?.display || rVehicle?.name) : vehicle?.name) || '—'}</div>
          </div>
          {showResearch && rVehicle ? (
            <div className="grid grid-cols-2 gap-3">
              <div><div className="text-xs text-gray-500">{t('procRtbYou')}</div><div className="font-semibold text-[#0A1A2F]">{t(YOU_KEY[rVehicle.you] || 'procRtbYouToJoin')}</div></div>
              <div><div className="text-xs text-gray-500">{t('procRtbDealsThroughIt')}</div>{rVehicle.deals_through_it ? <div className="font-mono font-semibold text-[#0A1A2F]">{fmt(rVehicle.deals_through_it)}</div> : <div className="text-sm text-gray-600">{t('procRtbNoPublishedCallOffs')}</div>}</div>
              <div><div className="text-xs text-gray-500">{t('procRtbCompetitorOnIt')}</div><div className="font-semibold text-[#0A1A2F]">{(rVehicle.competitors_on_it || []).slice(0, 3).join(', ') || '—'}</div></div>
              <div><div className="text-xs text-gray-500">{t('procRtbEndsLabel')}</div><div className="font-semibold text-[#0A1A2F]">{rVehicle.end_date ? String(rVehicle.end_date).slice(0, 7) : '—'}</div></div>
            </div>
          ) : null}
          {!showResearch && vehicle ? (
            <>
              <div className="space-y-2">
                <div className="text-xs text-gray-500">{t('procRtbWhoSellsThroughIt')}</div>
                <Bars items={vehicle.top_suppliers || []} />
              </div>
              <div className="space-y-2">
                <div className="text-xs text-gray-500">{t('procRtbResellersOnIt')}</div>
                <div className="flex flex-wrap gap-2">
                  {(vehicle.resellers || []).length ? vehicle.resellers.map((r) => {
                    const known = resellers.some((x) => x.name === r.name);
                    return (
                      <button
                        key={r.name}
                        type="button"
                        onClick={() => known && setResellerName(r.name)}
                        className={`min-h-[36px] px-3 rounded-full text-xs border ${known ? 'border-gray-300 bg-white hover:border-[#0A1A2F]' : 'border-dashed border-gray-300 bg-gray-50 cursor-default'}`}
                      >
                        {r.name} · {fmt(r.n)}
                      </button>
                    );
                  }) : <span className="text-sm text-gray-500">{t('procRtbNoResellerOnVehicle')}</span>}
                </div>
              </div>
              <div className="flex flex-wrap items-baseline gap-2 rounded-lg bg-gray-50 p-3">
                <span className="text-sm text-gray-500">{t('procRtbAreYouOnIt')}</span>
                <span className="font-semibold text-[#0A1A2F]">{hasOwnProducts ? (vehicle.on_it ? t('procRtbYesSoldThroughIt') : t('procRtbNotOnRecord')) : t('procRtbNotConfigured')}</span>
              </div>
            </>
          ) : null}
        </section>
      </div>

      <div className={`flex flex-wrap gap-4 items-start ${busy}`}>
        <section className="flex-[1_1_420px] min-w-0 rounded-xl border border-gray-200 bg-white p-5 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">{t('procRtbResellersIn', { country })}</h3>
            <LensToggle mine={mine} setMine={setMine} canMine={canMine} />
          </div>
          {mix && mix.route_known > 0 ? <div className="text-xs text-gray-500">{t('procRtbResellerShare', { n: mix.via_reseller ?? 0 })}</div> : null}
          {!loading && !resellers.length ? (
            <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-600">
              {mix && !(mix.route_known > 0) ? t('procRtbNotEnoughRouteDeals') : (mine ? t('procRtbNoResellerMine', { country }) : t('procRtbNoResellerAll', { country }))}
            </div>
          ) : null}
          {resellers.map((r) => (
            <button
              key={r.name}
              type="button"
              onClick={() => setResellerName(r.name)}
              className={`w-full text-left rounded-lg px-4 py-3 border space-y-1.5 ${reseller?.name === r.name ? 'border-2 border-[#00C2C2] bg-[#E6F8F8]' : 'border-gray-200 bg-white hover:border-gray-400'}`}
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-semibold text-[#0A1A2F]">{r.name}</span>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-700">
                <span><b className="font-mono">{fmt(r.deals)}</b> {t('procRtbDealsLower')}</span>
                <span><b className="font-mono">{fmt(r.buyers)}</b> {t('procRtbBuyersLower')}</span>
                <span><b className="font-mono">{fmt(r.makers)}</b> {t('procRtbMakersLower')}</span>
              </div>
            </button>
          ))}
        </section>

        <section className="flex-[1_1_420px] min-w-0 rounded-xl bg-[#0A1A2F] text-white p-5 space-y-4">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[#7FE3E3]">{t('procRtbInsideReseller')}</h3>
            <div className="text-lg font-bold">{reseller?.name || '—'}</div>
          </div>
          {reseller ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div><div className="font-mono text-lg">{fmt(reseller.deals)}</div><div className="text-xs text-gray-300">{t('procRtbDealsLower')}</div></div>
                <div><div className="font-mono text-lg">{fmt(reseller.buyers)}</div><div className="text-xs text-gray-300">{t('procRtbBuyersLower')}</div></div>
              </div>
              <div className="space-y-2">
                <div className="text-xs text-gray-300">{t('procRtbMakersItCarries')}</div>
                {(reseller.top_makers || []).length
                  ? <Bars items={reseller.top_makers} color="#00C2C2" track="bg-[#22344A]" />
                  : <div className="text-sm text-gray-300">{t('procRtbRecordNamesOnlyReseller')}</div>}
                <div className="text-xs text-gray-400">{t('procRtbMakerNamedPct', { n: reseller.maker_named_pct ?? 0 })}</div>
              </div>
              <div className="space-y-2">
                <div className="text-xs text-gray-300">{t('procRtbSellsThrough')}</div>
                <div className="flex flex-wrap gap-2">
                  {(reseller.vehicles || []).length
                    ? reseller.vehicles.map((v) => <span key={v.name} className="rounded-full bg-[#22344A] px-3 py-1 text-xs">{v.name} · {fmt(v.n)}</span>)
                    : <span className="text-sm text-gray-300">{t('procRtbDirectToBuyer')}</span>}
                </div>
              </div>
              <div className="rounded-lg bg-[#13263D] p-3 text-sm">
                {t('procRtbCarriesYourProduct')} <b>{hasOwnProducts ? (reseller.carries_you ? t('procYes') : t('procRtbNotOnRecord')) : t('procRtbNotConfigured')}</b>
              </div>
            </>
          ) : null}
        </section>
      </div>

      {data?.thresholds?.bands?.length ? <Thresholds country={country} t={data.thresholds} busy={busy} /> : null}

      <MarketSignals v={verdict} t={t} />
    </div>
  );
}

// The market's spend ladder, from the buyers' procurement profiles (medians) or the national procurement
// law (cited). Markets with no verified figures have no row and no tile — Tiago, 2026-10-14.
const BAND_COLORS = ['#00C2C2', '#6FA8DC', '#3D6A99', '#0A1A2F', '#5C6B7A'];
function money(cur, v) {
  if (v == null) return null;
  return `${cur}${Math.round(Number(v)).toLocaleString('en-GB')}`;
}
function bandRange(cur, b, t) {
  if (b.from == null && b.to != null) return t('procRtbUpTo', { amount: money(cur, b.to) });
  if (b.from != null && b.to == null) return t('procRtbAbove', { amount: money(cur, b.from) });
  if (b.from != null && b.to != null) return `${money(cur, b.from)} – ${money(cur, b.to)}`;
  return '—';
}
function Thresholds({ country, t, busy }) {
  // `t` here is the thresholds payload (prop name kept); the translator is `tl`.
  const { t: tl } = useTranslation();
  const cur = t.currency || '';
  return (
    <section className={`rounded-xl border border-gray-200 bg-white p-5 space-y-4 ${busy}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">{tl('procRtbHowBigBeforeTender')} · {country}</h3>
        {t.vat_basis ? <span className="text-xs text-gray-500">{t.vat_basis}</span> : null}
      </div>
      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(200px,1fr))]">
        {t.bands.map((b, i) => (
          <div key={b.label} className="border-l-4 pl-3 py-1 space-y-1" style={{ borderColor: BAND_COLORS[i % BAND_COLORS.length] }}>
            <div className="font-mono text-lg font-medium text-[#0A1A2F]">{bandRange(cur, b, tl)}</div>
            <div className="text-sm text-gray-700">{b.rule}</div>
            <div className="text-xs text-gray-500">
              {b.basis === 'profiles'
                ? `${tl('procRtbTypicalAcross', { n: fmt(b.buyers) })}${b.p25 != null && b.p75 != null && b.p25 !== b.p75 ? ` · ${tl('procRtbMostRange', { from: money(cur, b.p25), to: money(cur, b.p75) })}` : ''}`
                : b.source_url
                  ? <a href={b.source_url} target="_blank" rel="noopener noreferrer" className="underline hover:text-[#0A1A2F]">{b.source_label || tl('source')}</a>
                  : (b.source_label || tl('procRtbNationalRule'))}
            </div>
          </div>
        ))}
      </div>
      {t.note ? <div className="text-xs text-gray-500">{t.note}</div> : null}
    </section>
  );
}
